<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  DEFAULT_EXAMPLE_SLUG,
  EXAMPLE_CATALOG,
  getExampleBySlug,
  type ExampleCatalogEntry,
  type ExampleSlug,
} from '../examples/catalog'

const route = useRoute()
const router = useRouter()
const frameRef = useTemplateRef('frameRef')

const searchKeyword = ref('')
const frameRevision = ref(0)
const frameLoading = ref(true)
const frameVisible = ref(false)
const frameSrc = ref('')
const failedPreviewSlugs = ref(new Set<ExampleSlug>())
// 移动端视口下默认收起示例目录，避免遮挡右侧示例 iframe
const isMobileViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches
const isSidebarCollapsed = ref(isMobileViewport)

const activeSlug = computed(() => {
  const slug = route.params.slug
  return typeof slug === 'string' ? slug : DEFAULT_EXAMPLE_SLUG
})

const activeExample = computed<ExampleCatalogEntry>(() => {
  return getExampleBySlug(activeSlug.value) ?? EXAMPLE_CATALOG[0]
})

const filteredExamples = computed(() => {
  const keyword = searchKeyword.value.trim().toLowerCase()

  if (!keyword) {
    return EXAMPLE_CATALOG
  }

  return EXAMPLE_CATALOG.filter((entry) => {
    const haystack = [
      entry.title,
      entry.eyebrow,
      entry.description,
      entry.badge,
      ...entry.keywords,
    ]
      .join(' ')
      .toLowerCase()

    return haystack.includes(keyword)
  })
})

const currentFrameKey = computed(() => `${activeExample.value.slug}-${frameRevision.value}`)

function resolveCurrentFrameSrc(): string {
  if (typeof window === 'undefined') {
    return activeExample.value.routePath
  }

  const url = new URL(window.location.href)
  url.hash = activeExample.value.routePath
  return url.toString()
}

let frameReloadSequence = 0

function navigateFrameToBlank(frame: HTMLIFrameElement): Promise<void> {
  return new Promise((resolve) => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      window.clearTimeout(fallbackTimer)
      frame.removeEventListener('load', finish)
      resolve()
    }
    const fallbackTimer = window.setTimeout(finish, 250)
    frame.addEventListener('load', finish, { once: true })
    frame.src = 'about:blank'
  })
}

async function recreateFrame(): Promise<void> {
  const sequence = ++frameReloadSequence
  frameLoading.value = true
  const currentFrame = frameRef.value
  if (currentFrame) await navigateFrameToBlank(currentFrame)
  if (sequence !== frameReloadSequence) return

  frameVisible.value = false
  await nextTick()
  if (sequence !== frameReloadSequence) return

  frameRevision.value += 1
  frameSrc.value = resolveCurrentFrameSrc()
  frameVisible.value = true
}

watch(
  () => route.params.slug,
  (slug) => {
    const normalizedSlug = typeof slug === 'string' ? slug : DEFAULT_EXAMPLE_SLUG

    if (!getExampleBySlug(normalizedSlug)) {
      router.replace({ name: 'examples-hub', params: { slug: DEFAULT_EXAMPLE_SLUG } })
      return
    }

    void recreateFrame()
  },
  { immediate: true },
)

function activateExample(slug: ExampleSlug) {
  if (slug === activeExample.value.slug) {
    reloadExample()
    return
  }

  router.push({ name: 'examples-hub', params: { slug } })
}

function reloadExample() {
  void recreateFrame()
}

function handleFrameLoaded() {
  if (frameRef.value?.src === 'about:blank') return
  frameLoading.value = false
}

function shouldRenderPreviewImage(entry: ExampleCatalogEntry) {
  return Boolean(entry.previewImage) && !failedPreviewSlugs.value.has(entry.slug)
}

function handlePreviewImageError(slug: ExampleSlug) {
  failedPreviewSlugs.value = new Set([...failedPreviewSlugs.value, slug])
}

onBeforeUnmount(() => {
  frameReloadSequence += 1
})
</script>

<template>
  <div
    class="examples-shell"
    :class="{ 'sidebar-collapsed': isSidebarCollapsed }"
  >
    <aside class="examples-sidebar">
      <div class="examples-sidebar-panel">
        <header class="sidebar-header">
          <RouterLink class="sidebar-home-link" to="/">← 返回首页</RouterLink>
          <a class="sidebar-home-link" href="/benchmark/">性能测试 →</a>
          <div class="sidebar-header-tools">
            <span class="sidebar-badge">iframe isolation</span>
            <button
              class="panel-icon-button"
              type="button"
              aria-label="收起示例目录"
              :aria-expanded="!isSidebarCollapsed"
              @click="isSidebarCollapsed = true"
            >
              ‹
            </button>
          </div>
        </header>

        <div class="sidebar-search">
          <label class="sidebar-search-label" for="example-search">筛选示例</label>
          <input
            id="example-search"
            v-model="searchKeyword"
            class="sidebar-search-input"
            type="search"
            placeholder="搜索 point cloud / gaussian / load..."
          />
          <p class="sidebar-search-hint">
            每个示例都运行在独立 iframe 中，切换时会重新创建自己的渲染上下文。
          </p>
        </div>

        <div class="example-card-list">
          <button
            v-for="entry in filteredExamples"
            :key="entry.slug"
            class="example-card"
            :class="{ active: entry.slug === activeExample.slug }"
            type="button"
            @click="activateExample(entry.slug)"
          >
            <div
              class="example-card-preview"
              :class="[entry.previewClassName, { 'has-image': shouldRenderPreviewImage(entry) }]"
            >
              <img
                v-if="shouldRenderPreviewImage(entry)"
                class="example-card-preview-image"
                :src="entry.previewImage"
                :alt="entry.title"
                loading="lazy"
                @error="handlePreviewImageError(entry.slug)"
              />
              <span class="example-card-chip">{{ entry.badge }}</span>
              <span class="example-card-watermark">{{ entry.eyebrow }}</span>
            </div>

            <div class="example-card-body">
              <p class="example-card-eyebrow">{{ entry.eyebrow }}</p>
              <h2 class="example-card-title">{{ entry.title }}</h2>
              <p class="example-card-description">{{ entry.description }}</p>
            </div>
          </button>
        </div>
      </div>
    </aside>

    <section class="viewer-stage">
      <div class="viewer-frame-shell">
        <button
          v-if="isSidebarCollapsed"
          class="floating-panel-button sidebar-expand-button"
          type="button"
          aria-label="展开示例目录"
          :aria-expanded="!isSidebarCollapsed"
          @click="isSidebarCollapsed = false"
        >
          <span>☰</span> 示例目录
        </button>

        <div v-if="frameLoading" class="viewer-frame-loading">
          <div class="viewer-frame-loading-grid"></div>
          <p class="viewer-frame-loading-text">正在重新创建 {{ activeExample.title }} 的独立上下文...</p>
        </div>

        <iframe
          v-if="frameVisible"
          ref="frameRef"
          :key="currentFrameKey"
          class="viewer-frame"
          :src="frameSrc"
          :title="`${activeExample.title} example`"
          loading="eager"
          @load="handleFrameLoaded"
        />
      </div>
    </section>
  </div>
</template>

<style scoped>
.examples-shell {
  --sidebar-width: clamp(300px, 22vw, 380px);
  display: grid;
  grid-template-columns: var(--sidebar-width) minmax(0, 1fr);
  height: 100dvh;
  min-height: 100vh;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(48, 79, 126, 0.22), transparent 30%),
    radial-gradient(circle at bottom right, rgba(40, 118, 99, 0.2), transparent 32%),
    #0a0f16;
  color: #edf2f8;
  transition: grid-template-columns 180ms ease;
}

.examples-shell.sidebar-collapsed {
  --sidebar-width: 0px;
}

.examples-sidebar {
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  padding: 0;
  box-sizing: border-box;
  transition:
    padding 180ms ease,
    opacity 120ms ease;
}

.sidebar-collapsed .examples-sidebar {
  padding: 0;
  opacity: 0;
  pointer-events: none;
}

.examples-sidebar-panel {
  display: flex;
  height: 100%;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  min-height: 0;
  padding: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(12, 18, 28, 0.98) 0%, rgba(8, 12, 19, 0.98) 100%);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.24);
  box-sizing: border-box;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-width: 0;
}

.sidebar-home-link {
  color: rgba(210, 235, 247, 0.72);
  font-size: 11px;
  text-decoration: none;
}

.sidebar-home-link:hover {
  color: #c8efff;
}

.sidebar-header-tools {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sidebar-badge {
  padding: 5px 8px;
  border: 1px solid rgba(137, 212, 255, 0.24);
  border-radius: 999px;
  color: #9fd8ff;
  font-size: 8px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
}

.panel-icon-button {
  display: inline-grid;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  place-items: center;
  padding: 0;
  border: 1px solid rgba(136, 210, 247, 0.14);
  border-radius: 9px;
  background: rgba(75, 157, 204, 0.07);
  color: rgba(185, 229, 249, 0.72);
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
}

.panel-icon-button:hover {
  border-color: rgba(136, 210, 247, 0.3);
  background: rgba(75, 157, 204, 0.14);
  color: #d6f3ff;
}

.sidebar-search {
  min-width: 0;
  padding: 14px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
}

.sidebar-search-label {
  display: block;
  margin-bottom: 10px;
  color: rgba(237, 242, 248, 0.86);
  font-size: 13px;
}

.sidebar-search-input {
  width: 100%;
  padding: 12px 13px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  background: rgba(4, 8, 14, 0.9);
  color: #f7fbff;
  font-size: 14px;
  outline: none;
  transition:
    border-color 140ms ease,
    box-shadow 140ms ease;
}

.sidebar-search-input:focus {
  border-color: rgba(137, 212, 255, 0.4);
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.14);
}

.sidebar-search-hint {
  margin: 10px 0 0;
  color: rgba(222, 231, 241, 0.62);
  font-size: 12px;
  line-height: 1.6;
}

.example-card-list {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  overflow-y: auto;
  padding-right: 4px;
}

.example-card-list::-webkit-scrollbar {
  width: 8px;
}

.example-card-list::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
}

.example-card {
  flex: 0 0 auto;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.02);
  color: inherit;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  transition:
    transform 160ms ease,
    border-color 160ms ease,
    box-shadow 160ms ease,
    background 160ms ease;
}

.example-card:hover {
  transform: translateY(-2px);
  border-color: rgba(149, 196, 255, 0.22);
  box-shadow: 0 16px 28px rgba(0, 0, 0, 0.18);
}

.example-card.active {
  border-color: rgba(120, 180, 255, 0.46);
  background: rgba(80, 139, 255, 0.08);
  box-shadow: 0 0 0 1px rgba(120, 180, 255, 0.18);
}

.example-card-preview {
  position: relative;
  height: 150px;
  overflow: hidden;
  isolation: isolate;
}

.example-card-preview::before,
.example-card-preview::after {
  position: absolute;
  content: '';
  inset: 0;
}

.example-card-preview::before {
  z-index: 1;
  background:
    linear-gradient(140deg, rgba(255, 255, 255, 0.08), transparent 52%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.04), transparent);
}

.example-card-preview::after {
  inset: auto 14px 14px 14px;
  z-index: 1;
  height: 38px;
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(5, 8, 12, 0), rgba(5, 8, 12, 0.82));
}

.example-card-preview.has-image {
  background: #05080d;
}

.example-card-preview.has-image::before {
  background: none;
}

.example-card-preview.has-image::after {
  inset: auto 0 0 0;
  height: 46px;
  border-radius: 0;
  background: linear-gradient(180deg, rgba(5, 8, 12, 0), rgba(5, 8, 12, 0.62));
}

.example-card-preview-image {
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.preview-potree {
  background:
    radial-gradient(circle at 18% 22%, rgba(119, 248, 187, 0.86), transparent 16%),
    radial-gradient(circle at 66% 34%, rgba(255, 255, 255, 0.88), transparent 18%),
    radial-gradient(circle at 78% 70%, rgba(78, 140, 255, 0.82), transparent 16%),
    linear-gradient(180deg, #05070b 0%, #0d1420 100%);
}

.preview-potree::before {
  background:
    radial-gradient(circle, rgba(255, 255, 255, 0.75) 0 1.1px, transparent 1.6px),
    radial-gradient(circle, rgba(144, 255, 188, 0.78) 0 1.2px, transparent 1.8px),
    radial-gradient(circle, rgba(90, 150, 255, 0.68) 0 1.2px, transparent 1.8px);
  background-size: 20px 20px, 32px 32px, 44px 44px;
  background-position: 0 0, 10px 8px, 4px 14px;
  opacity: 0.88;
}

.preview-gs3d {
  background:
    radial-gradient(circle at 20% 20%, rgba(255, 181, 88, 0.86), transparent 18%),
    radial-gradient(circle at 70% 26%, rgba(102, 183, 255, 0.84), transparent 22%),
    radial-gradient(circle at 62% 74%, rgba(255, 88, 165, 0.58), transparent 24%),
    linear-gradient(135deg, #080810 0%, #17192a 60%, #081017 100%);
}

.preview-gs3d::before {
  background:
    radial-gradient(circle at 24% 58%, rgba(255, 255, 255, 0.16) 0 8%, transparent 9%),
    radial-gradient(circle at 58% 50%, rgba(255, 255, 255, 0.13) 0 12%, transparent 13%),
    radial-gradient(circle at 82% 30%, rgba(255, 255, 255, 0.14) 0 10%, transparent 11%);
  filter: blur(1px);
}

.preview-load-cloud {
  background:
    linear-gradient(180deg, rgba(87, 158, 255, 0.26), transparent 55%),
    linear-gradient(180deg, #0b1118 0%, #0f1823 100%);
}

.preview-load-cloud::before {
  background:
    linear-gradient(90deg, rgba(128, 161, 215, 0.14) 1px, transparent 1px),
    linear-gradient(rgba(128, 161, 215, 0.14) 1px, transparent 1px);
  background-size: 26px 26px;
  transform: perspective(480px) rotateX(62deg) scale(1.6) translateY(24px);
  transform-origin: center bottom;
}

.preview-mesh-helmet {
  background:
    radial-gradient(circle at 50% 42%, rgba(150, 174, 202, 0.5), transparent 20%),
    radial-gradient(circle at 50% 58%, rgba(171, 76, 50, 0.36), transparent 30%),
    linear-gradient(145deg, #111b29, #080b10 72%);
}

.preview-mesh-helmet::before {
  inset: 23px 72px 18px;
  border: 1px solid rgba(193, 219, 241, 0.3);
  border-radius: 48% 48% 34% 34%;
  background:
    radial-gradient(ellipse at 30% 52%, rgba(3, 7, 12, 0.92) 0 18%, transparent 19%),
    radial-gradient(ellipse at 70% 52%, rgba(3, 7, 12, 0.92) 0 18%, transparent 19%),
    linear-gradient(150deg, rgba(163, 190, 216, 0.72), rgba(88, 55, 45, 0.76));
  transform: perspective(180px) rotateX(-5deg);
}

.preview-mesh-horse {
  background:
    radial-gradient(circle at 54% 50%, rgba(201, 140, 83, 0.26), transparent 28%),
    linear-gradient(155deg, #182333, #080d14 70%);
}

.preview-mesh-horse::before {
  inset: 42px 48px 35px 56px;
  border-radius: 48% 62% 42% 50%;
  background: linear-gradient(135deg, rgba(224, 169, 112, 0.88), rgba(102, 61, 38, 0.72));
  box-shadow:
    48px -26px 0 -22px rgba(212, 153, 96, 0.84),
    -32px 35px 0 -24px rgba(212, 153, 96, 0.72),
    40px 35px 0 -24px rgba(212, 153, 96, 0.72);
  transform: rotate(-6deg);
}

.preview-three-lines {
  background:
    linear-gradient(135deg, rgba(28, 190, 153, 0.28), transparent 36%),
    linear-gradient(225deg, rgba(80, 123, 255, 0.3), transparent 38%),
    #091018;
}

.preview-three-lines::before {
  background:
    linear-gradient(120deg, transparent 45%, rgba(145, 228, 255, 0.8) 48%, transparent 51%),
    linear-gradient(150deg, transparent 52%, rgba(255, 255, 255, 0.6) 55%, transparent 58%),
    linear-gradient(95deg, transparent 60%, rgba(140, 171, 255, 0.78) 62%, transparent 64%);
  opacity: 0.88;
}

.preview-multi-viewport {
  background:
    linear-gradient(135deg, rgba(89, 157, 255, 0.28), transparent 34%),
    linear-gradient(225deg, rgba(58, 207, 181, 0.18), transparent 36%),
    #091018;
}

.preview-multi-viewport::before {
  background:
    linear-gradient(90deg, transparent 0 52%, rgba(255, 255, 255, 0.08) 52% 54%, transparent 54%),
    linear-gradient(180deg, transparent 0 48%, rgba(255, 255, 255, 0.08) 48% 50%, transparent 50%),
    radial-gradient(circle at 22% 26%, rgba(123, 217, 255, 0.9) 0 4%, transparent 5%),
    radial-gradient(circle at 76% 30%, rgba(255, 179, 106, 0.88) 0 5%, transparent 6%),
    radial-gradient(circle at 74% 76%, rgba(115, 244, 209, 0.9) 0 5%, transparent 6%);
  opacity: 0.92;
}

.preview-cad-multi-viewport {
  background:
    linear-gradient(135deg, rgba(96, 167, 255, 0.22), transparent 32%),
    linear-gradient(225deg, rgba(255, 191, 124, 0.18), transparent 34%),
    #091018;
}

.preview-cad-multi-viewport::before {
  background:
    linear-gradient(90deg, transparent 0 49%, rgba(255, 255, 255, 0.08) 49% 51%, transparent 51%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0 1px, transparent 1px 100%),
    radial-gradient(circle at 24% 28%, rgba(142, 208, 255, 0.92) 0 5%, transparent 6%),
    radial-gradient(circle at 74% 72%, rgba(255, 191, 124, 0.92) 0 5%, transparent 6%),
    linear-gradient(0deg, transparent 0 76%, rgba(255, 255, 255, 0.08) 76% 78%, transparent 78%);
  opacity: 0.92;
}

.example-card-chip,
.example-card-watermark {
  position: absolute;
  z-index: 2;
}

.example-card-chip {
  top: 14px;
  left: 14px;
  padding: 7px 10px;
  border-radius: 999px;
  background: rgba(9, 14, 20, 0.72);
  backdrop-filter: blur(12px);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.example-card-watermark {
  right: 14px;
  bottom: 16px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 13px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.example-card-body {
  padding: 16px 16px 18px;
}

.example-card-eyebrow {
  margin: 0 0 8px;
  color: rgba(153, 213, 255, 0.86);
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.example-card-title {
  margin: 0;
  font-size: 20px;
  line-height: 1.15;
}

.example-card-description {
  margin: 10px 0 0;
  color: rgba(231, 237, 245, 0.68);
  font-size: 13px;
  line-height: 1.65;
}

.viewer-stage {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  padding: 0;
}

.viewer-frame-shell {
  position: relative;
  flex: 1;
  min-height: 0;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0;
  overflow: hidden;
  background:
    radial-gradient(circle at top, rgba(81, 120, 190, 0.2), transparent 36%),
    #05080d;
}

.floating-panel-button {
  position: absolute;
  z-index: 4;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 13px;
  border: 1px solid rgba(132, 207, 244, 0.16);
  border-radius: 11px;
  background: rgba(9, 16, 24, 0.72);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.2);
  color: rgba(205, 237, 250, 0.78);
  cursor: pointer;
  font-size: 10px;
  backdrop-filter: blur(12px);
}

.floating-panel-button:hover {
  border-color: rgba(132, 207, 244, 0.32);
  background: rgba(15, 29, 41, 0.84);
  color: #d9f5ff;
}

.sidebar-expand-button {
  top: 14px;
  left: 14px;
}

.viewer-frame {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
  background: transparent;
}

.viewer-frame-loading {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  background: linear-gradient(180deg, rgba(7, 11, 16, 0.92), rgba(7, 11, 16, 0.76));
  pointer-events: none;
}

.viewer-frame-loading-grid {
  width: 72px;
  height: 72px;
  border-radius: 24px;
  background:
    linear-gradient(90deg, rgba(108, 152, 255, 0.18) 1px, transparent 1px),
    linear-gradient(rgba(108, 152, 255, 0.18) 1px, transparent 1px),
    linear-gradient(135deg, rgba(137, 212, 255, 0.3), rgba(61, 101, 177, 0.12));
  background-size: 18px 18px, 18px 18px, cover;
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.06),
    0 14px 36px rgba(0, 0, 0, 0.24);
  animation: pulse-grid 1.4s ease-in-out infinite;
}

.viewer-frame-loading-text {
  margin: 0;
  color: rgba(232, 239, 247, 0.82);
  font-size: 14px;
}

@keyframes pulse-grid {
  0%,
  100% {
    transform: scale(0.96);
    opacity: 0.84;
  }

  50% {
    transform: scale(1);
    opacity: 1;
  }
}

@media (max-width: 760px) {
  .examples-shell,
  .examples-shell.sidebar-collapsed {
    grid-template-columns: minmax(0, 1fr);
  }

  .examples-sidebar {
    position: absolute;
    inset: 0 auto 0 0;
    z-index: 8;
    width: min(360px, calc(100vw - 28px));
    padding: 12px;
  }

  .viewer-stage {
    padding: 0;
  }

  .sidebar-expand-button {
    top: 10px;
  }

  .sidebar-expand-button {
    left: 10px;
  }
}

</style>
